import React, { useState, useEffect } from "react";
import api from "../api";
import Table from "../components/Table";
import Modal from "../components/Modal";
import ImageUpload from "../components/ImageUpload";
import {
  FiPlus,
  FiTrash2,
  FiFileText,
  FiTag,
  FiCalendar,
  FiLink,
  FiUsers,
  FiBook,
  FiLayers,
  FiList,
  FiBookOpen,
  FiDollarSign,
} from "react-icons/fi";
import toast from "react-hot-toast";
const FormGroup = ({ children, cols = 1 }) => {
  const colClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-3",
  };
  return (
    <div className={`grid ${colClasses[cols]} gap-6 mb-6`}>{children}</div>
  );
};

const InputField = ({
  icon,
  label,
  name,
  type = "text",
  value,
  onChange,
  required = true,
  options,
}) => (
  <div>
    <label
      htmlFor={name}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <div className="mt-1 relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {icon}
      </div>
      {options ? (
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
        >
          <option value="">Select an option</option>
          {options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          id={name}
          value={value}
          onChange={onChange}
          className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
          required={required}
        />
      )}
    </div>
  </div>
);
const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [formData, setFormData] = useState({
    projectName: "",
    projectDescription: "",
    projectLevel: "",
    projectCategory: "",
    status: "",
    images: [],
    links: [],
    team: [],
    tags: [],
    colleges: [],
    department: "",
    startDate: "",
    endDate: "",
    isGranted: "no",
    grantedAmount: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchProjects();
    fetchColleges();
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchProjects = async () => {
    try {
      const response = await api.get("/project", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      setProjects(response.data.data);
      setPagination((prevState) => ({
        ...prevState,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));
    } catch (error) {
      toast.error("Failed to fetch projects");
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await api.get("/college");
      setColleges(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch colleges");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prevState) => ({ ...prevState, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prevState) => ({ ...prevState, limit: newLimit, page: 1 }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination((prevState) => ({ ...prevState, page: 1 }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  };

  const handleArrayInputChange = (e, field) => {
    const values = e.target.value.split(",").map((item) => item.trim());
    setFormData((prevState) => ({ ...prevState, [field]: values }));
  };

  const handleImageUpload = (urls) => {
    setFormData((prevState) => ({
      ...prevState,
      images: [...prevState.images, ...urls],
    }));
  };

  const handleImageRemove = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      images: prevState.images.filter((_, i) => i !== index),
    }));
  };

  const handleTeamMemberChange = (index, field, value) => {
    const updatedTeam = [...formData.team];
    updatedTeam[index] = { ...updatedTeam[index], [field]: value };
    setFormData((prevState) => ({ ...prevState, team: updatedTeam }));
  };

  const handleTeamMemberPhotoUpload = (index, urls) => {
    handleTeamMemberChange(index, "photo", urls[0]);
  };

  const handleTeamMemberPhotoRemove = (index) => {
    handleTeamMemberChange(index, "photo", "");
  };

  const addTeamMember = () => {
    setFormData((prevState) => ({
      ...prevState,
      team: [
        ...prevState.team,
        { name: "", photo: "", linkedinUrl: "", batchYear: "", department: "" },
      ],
    }));
  };

  const removeTeamMember = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      team: prevState.team.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSubmit = { ...formData };
      if (dataToSubmit.isGranted === "no") {
        dataToSubmit.grantedAmount = 0;
      }
      if (currentProject) {
        await api.put(`/project/${currentProject._id}`, dataToSubmit);
        toast.success("Project updated successfully");
      } else {
        await api.post("/project", dataToSubmit);
        toast.success("Project created successfully");
      }
      setIsModalOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error("Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setCurrentProject(project);
    setFormData({
      projectName: project.projectName,
      projectDescription: project.projectDescription,
      projectLevel: project.projectLevel,
      projectCategory: project.projectCategory,
      status: project.status,
      images: project.images,
      links: project.links,
      team: project.team,
      tags: project.tags,
      colleges: project.colleges
        ? project.colleges.map((c) => c._id)
        : project.college
        ? [project.college._id]
        : [],
      department: project.department,
      startDate: project.startDate?.split("T")[0] || "",
      endDate: project.endDate ? project.endDate.split("T")[0] : "",
      isGranted: project.isGranted,
      grantedAmount: project.grantedAmount,
    });
    setIsModalOpen(true);
  };

  const handleCollegeToggle = (collegeId) => {
    setFormData((prevState) => {
      const isSelected = prevState.colleges.includes(collegeId);
      if (isSelected) {
        return {
          ...prevState,
          colleges: prevState.colleges.filter((id) => id !== collegeId),
        };
      } else {
        return { ...prevState, colleges: [...prevState.colleges, collegeId] };
      }
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await api.delete(`/project/${id}`);
        toast.success("Project deleted successfully");
        fetchProjects();
      } catch (error) {
        toast.error("Failed to delete project");
      }
    }
  };

  const columns = [
    { key: "projectName", label: "Project Name" },
    { key: "projectLevel", label: "Project Level" },
    { key: "status", label: "Status" },
    {
      key: "college",
      label: "College",
      render: (item) =>
        item.colleges
          ? item.colleges.map((c) => c.name).join(", ")
          : item.college?.name || "N/A",
    },
    {
      key: "department",
      label: "Department",
      render: (item) => item.department || "N/A",
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (item) => new Date(item.startDate).toLocaleDateString(),
    },
    {
      key: "isGranted",
      label: "Granted",
      render: (item) => (item.isGranted === "yes" ? "Yes" : "No"),
    },
    {
      key: "grantedAmount",
      label: "Granted Amount",
      render: (item) =>
        item.isGranted === "yes"
          ? `$${item.grantedAmount?.toLocaleString() || "0"}`
          : "-",
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
        <button
          onClick={() => {
            setCurrentProject(null);
            setFormData({
              projectName: "",
              projectDescription: "",
              projectLevel: "",
              projectCategory: "",
              status: "",
              images: [],
              links: [],
              team: [],
              tags: [],
              colleges: [],
              department: "",
              startDate: "",
              endDate: "",
              isGranted: "no",
              grantedAmount: 0,
            });
            setIsModalOpen(true);
          }}
          className="px-4 py-2 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
        >
          <FiPlus className="inline-block mr-2" />
          Add Project
        </button>
      </div>
      <Table
        columns={columns}
        data={projects}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSearch={handleSearch}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentProject ? "Edit Project" : "Add Project"}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormGroup cols={2}>
            <InputField
              icon={<FiFileText className="h-5 w-5 text-gray-400" />}
              label="Project Name"
              name="projectName"
              value={formData.projectName}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiLayers className="h-5 w-5 text-gray-400" />}
              label="Project Level"
              name="projectLevel"
              value={formData.projectLevel}
              onChange={handleInputChange}
              options={[
                { value: "undergraduate", label: "Undergraduate" },
                { value: "graduate", label: "Graduate" },
                { value: "postgraduate", label: "Postgraduate" },
                { value: "faculty", label: "Faculty" },
              ]}
            />
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiList className="h-5 w-5 text-gray-400" />}
              label="Project Category"
              name="projectCategory"
              value={formData.projectCategory}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              options={[
                { value: "ongoing", label: "Ongoing" },
                { value: "completed", label: "Completed" },
              ]}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label
                htmlFor="projectDescription"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Project Description
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                  <FiFileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  id="projectDescription"
                  name="projectDescription"
                  rows="3"
                  value={formData.projectDescription}
                  onChange={handleInputChange}
                  required
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                ></textarea>
              </div>
            </div>
          </FormGroup>
          <FormGroup cols={2}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Colleges
              </label>
              <div className="mt-1 border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                {colleges.length === 0 ? (
                  <p className="text-gray-500 text-sm">No colleges available</p>
                ) : (
                  <div className="space-y-2">
                    {colleges.map((college) => (
                      <label
                        key={college._id}
                        className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.colleges.includes(college._id)}
                          onChange={() => handleCollegeToggle(college._id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {college.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {formData.colleges.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {formData.colleges.length} college(s) selected
                </p>
              )}
            </div>
            <InputField
              icon={<FiBook className="h-5 w-5 text-gray-400" />}
              label="Department"
              name="department"
              value={formData.department}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup cols={2}>
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="Start Date"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="End Date"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
              required={false}
            />
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Is Granted"
              name="isGranted"
              value={formData.isGranted}
              onChange={handleInputChange}
              options={[
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]}
            />
            {formData.isGranted === "yes" && (
              <InputField
                icon={<FiDollarSign className="h-5 w-5 text-gray-400" />}
                label="Granted Amount"
                name="grantedAmount"
                type="number"
                value={formData.grantedAmount}
                onChange={handleInputChange}
              />
            )}
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiLink className="h-5 w-5 text-gray-400" />}
              label="Links (comma-separated)"
              name="links"
              value={formData.links.join(", ")}
              onChange={(e) => handleArrayInputChange(e, "links")}
              required={false}
            />
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Tags (comma-separated)"
              name="tags"
              value={formData.tags.join(", ")}
              onChange={(e) => handleArrayInputChange(e, "tags")}
              required={false}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Images
              </label>
              <ImageUpload
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
                images={formData.images}
                multiple
              />
            </div>
          </FormGroup>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Team Members
            </label>
            {formData.team.map((member, index) => (
              <div key={index} className="mt-2 p-4 border rounded-md shadow-sm">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-lg">
                    Team Member {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeTeamMember(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FiTrash2 className="h-5 w-5" />
                  </button>
                </div>
                <FormGroup cols={2}>
                  <InputField
                    icon={<FiUsers className="h-5 w-5 text-gray-400" />}
                    label="Name"
                    name={`team-${index}-name`}
                    value={member.name}
                    onChange={(e) =>
                      handleTeamMemberChange(index, "name", e.target.value)
                    }
                  />
                  <InputField
                    icon={<FiLink className="h-5 w-5 text-gray-400" />}
                    label="LinkedIn URL"
                    name={`team-${index}-linkedinUrl`}
                    value={member.linkedinUrl}
                    onChange={(e) =>
                      handleTeamMemberChange(
                        index,
                        "linkedinUrl",
                        e.target.value
                      )
                    }
                  />
                </FormGroup>
                <FormGroup cols={2}>
                  <InputField
                    icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
                    label="Batch Year"
                    name={`team-${index}-batchYear`}
                    value={member.batchYear}
                    onChange={(e) =>
                      handleTeamMemberChange(index, "batchYear", e.target.value)
                    }
                  />
                  <InputField
                    icon={<FiBookOpen className="h-5 w-5 text-gray-400" />}
                    label="Department"
                    name={`team-${index}-department`}
                    value={member.department}
                    onChange={(e) =>
                      handleTeamMemberChange(
                        index,
                        "department",
                        e.target.value
                      )
                    }
                  />
                </FormGroup>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Photo
                  </label>
                  <ImageUpload
                    onUpload={(urls) =>
                      handleTeamMemberPhotoUpload(index, urls)
                    }
                    onRemove={() => handleTeamMemberPhotoRemove(index)}
                    images={member.photo ? [member.photo] : []}
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addTeamMember}
              className="mt-4 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiPlus className="inline-block mr-2" />
              Add Team Member
            </button>
          </div>
          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {currentProject ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Projects;
